/**
 * TaskSection.jsx
 *
 * Whitelist application flow, rebuilt.
 *
 * ── How a task completes ────────────────────────────────────────────
 * Tasks are NOT hand-ticked any more. That was the bug: the checkbox
 * was the control, so a user could tick everything without opening a
 * single link, and clicking the tick itself was fiddly and unreliable.
 *
 * Now the flow is: press OPEN → the link opens and a 60-second
 * verification countdown starts → the task flips to VERIFIED on its own.
 * The tick is pure output, never an input, so there is nothing to
 * mis-click.
 *
 * To be clear about what this is: a delay with a progress ring, not a
 * real check of whether the user followed or retweeted. Verifying that
 * needs the X API and a server. It raises the effort of faking entries
 * and sets the right expectation, which is why every submission still
 * lands as 'pending' for a human to approve.
 *
 * ── Submission ──────────────────────────────────────────────────────
 * The wallet form does not exist until all tasks are VERIFIED, and on
 * success the whole page is replaced by a confirmation screen rather
 * than appending a box underneath.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Panel from '../Panel';
import GlowText from '../GlowText';
import RoboticButton from '../RoboticButton';
import { submitTasks } from '../../lib/tasks';

// ── Campaign links ──────────────────────────────────────────────────
const PROFILE_URL  = 'https://x.com/HexaBots_';
const ENGAGE_TWEET = 'https://x.com/HexaBots_/status/2074489626438643803?s=20';
const QUOTE_TWEET  = 'https://x.com/HexaBots_/status/2099155863659221054?s=20';
const TELEGRAM_URL = 'https://t.me/HEXABOTSOFFICIALS';

/** Seconds a task spends in verification after its link is opened. */
const VERIFY_SECONDS = 60;

const XIcon = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor" aria-hidden="true">
    <path d="M18.244 2H21l-6.52 7.45L22.5 22h-6.83l-4.77-6.24L5.2 22H2.44l6.98-7.97L1.5 2h6.98l4.32 5.72L18.244 2Zm-1.19 18h1.71L7.05 4H5.24l11.82 16Z" />
  </svg>
);
const TgIcon = () => (
  <svg viewBox="0 0 24 24" width="17" height="17" fill="currentColor" aria-hidden="true">
    <path d="M21.94 4.6c.27-1.18-.94-2.13-2.02-1.68L2.4 10.13c-1.25.5-1.24 2.26.02 2.74l4.4 1.68 1.7 5.44c.2.63 1 .82 1.47.35l2.4-2.38 4.36 3.24c.98.72 2.38.19 2.65-1L21.94 4.6ZM8.6 13.6l9.5-6.72c.34-.24.7.2.4.48l-7.9 7.35c-.3.28-.5.66-.56 1.07l-.28 1.9-1.16-4.08Z" />
  </svg>
);

const TASKS = [
  { id: 'follow',   n: 1, label: 'Follow @HexaBots_',      hint: 'Follow the official account on X',  url: PROFILE_URL,  Icon: XIcon },
  { id: 'like',     n: 2, label: 'Like the post',          hint: 'Like the campaign post',            url: ENGAGE_TWEET, Icon: XIcon },
  { id: 'retweet',  n: 3, label: 'Repost',                 hint: 'Retweet the campaign post',         url: ENGAGE_TWEET, Icon: XIcon },
  { id: 'comment',  n: 4, label: 'Comment',                hint: 'Leave a reply on the post',         url: ENGAGE_TWEET, Icon: XIcon },
  { id: 'telegram', n: 5, label: 'Join Telegram',          hint: 'Join the official group',           url: TELEGRAM_URL, Icon: TgIcon },
  { id: 'quote',    n: 6, label: 'Quote tweet',            hint: 'Quote the post, then paste the link below', url: QUOTE_TWEET, Icon: XIcon },
];

const EVM_RE = /^0x[a-fA-F0-9]{40}$/;
const TWEET_RE = /^https?:\/\/(www\.)?(x|twitter)\.com\/.+/i;

/* ── Countdown ring shown while a task verifies ──────────────────── */
function VerifyRing({ remaining }) {
  const R = 13;
  const C = 2 * Math.PI * R;
  const pct = remaining / VERIFY_SECONDS;
  return (
    <div style={{ position: 'relative', width: 32, height: 32 }}>
      <svg width="32" height="32" className="verify-ring">
        <circle cx="16" cy="16" r={R} fill="none" stroke="var(--steel)" strokeWidth="2.5" />
        <circle
          cx="16" cy="16" r={R} fill="none"
          stroke="var(--amber)" strokeWidth="2.5" strokeLinecap="round"
          strokeDasharray={C} strokeDashoffset={C * (1 - pct)}
          style={{ filter: 'drop-shadow(0 0 4px var(--amber))' }}
        />
      </svg>
      <span
        className="font-mono-hud"
        style={{
          position: 'absolute', inset: 0, display: 'flex',
          alignItems: 'center', justifyContent: 'center',
          fontSize: 9, color: 'var(--amber)',
        }}
      >
        {remaining}
      </span>
    </div>
  );
}

/* ── One task row ────────────────────────────────────────────────── */
function TaskRow({ task, state, remaining, onOpen, index }) {
  const status = state?.status || 'idle';
  const isVerifying = status === 'verifying';
  const isDone = status === 'verified';

  const borderColor = isDone ? 'var(--cyan)' : isVerifying ? 'var(--amber)' : 'var(--steel)';

  return (
    <div
      className={`mech-panel row-in ${isVerifying ? 'verify-glow' : ''} ${isDone ? 'row-verified' : ''}`}
      style={{
        position: 'relative', overflow: 'hidden',
        padding: '13px 15px', borderColor,
        display: 'flex', alignItems: 'center', gap: 13,
        animationDelay: `${index * 55}ms`,
      }}
    >
      {isVerifying && <span className="verify-sweep" />}

      {/* Step number / state badge */}
      <div
        className="shrink-0 flex items-center justify-center font-mono-hud"
        style={{
          width: 30, height: 30, borderRadius: 7,
          border: `1px solid ${borderColor}`,
          background: isDone ? 'rgba(0,229,255,0.14)' : 'rgba(10,15,28,0.6)',
          color: isDone ? 'var(--cyan)' : 'var(--text-dim)',
          fontSize: 13,
        }}
      >
        {isDone
          ? <span className="tick-stamp" style={{ fontSize: 15, lineHeight: 1 }}>✓</span>
          : task.n}
      </div>

      {/* Label */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span style={{ color: isDone ? 'var(--cyan)' : 'var(--text-dim)', display: 'flex' }}>
            <task.Icon />
          </span>
          <span
            className="font-display"
            style={{
              fontSize: 'clamp(12px,2vw,14px)', fontWeight: 700, letterSpacing: '0.04em',
              color: isDone ? 'var(--text)' : 'var(--text)',
            }}
          >
            {task.label}
          </span>
        </div>
        <div className="font-mono-hud text-dim" style={{ fontSize: 11, marginTop: 2 }}>
          {isVerifying ? 'Verifying your action…' : isDone ? 'Verified' : task.hint}
        </div>
      </div>

      {/* Control */}
      <div className="shrink-0">
        {isVerifying ? (
          <VerifyRing remaining={remaining} />
        ) : isDone ? (
          <span
            className="font-mono-hud tick-stamp"
            style={{ fontSize: 11, color: 'var(--cyan)', letterSpacing: '0.1em' }}
          >
            DONE
          </span>
        ) : (
          <button
            type="button"
            onClick={() => onOpen(task)}
            className="btn-robotic btn-robotic-cyan"
            style={{ padding: '7px 14px', fontSize: 10 }}
          >
            OPEN
            <span className="btn-sheen" aria-hidden="true" />
          </button>
        )}
      </div>
    </div>
  );
}

/* ── Success screen (replaces the page, not appended below) ──────── */
function SuccessScreen({ wallet, alreadyExists }) {
  return (
    <Panel title="APPLICATION // RECEIVED">
      <div className="success-in flex flex-col items-center text-center gap-6 py-10 px-2">
        <div style={{ position: 'relative', width: 104, height: 104 }}>
          <span className="success-ring" />
          <span className="success-ring" />
          <span className="success-ring" />
          <div
            className="flex items-center justify-center"
            style={{
              position: 'absolute', inset: 8,
              border: '2px solid var(--cyan)',
              clipPath: 'polygon(30% 0%, 70% 0%, 100% 50%, 70% 100%, 30% 100%, 0% 50%)',
              background: 'radial-gradient(circle at 40% 30%, rgba(0,229,255,0.22), rgba(10,15,28,0.95))',
              boxShadow: '0 0 34px rgba(0,229,255,0.45)',
            }}
          >
            <GlowText text="✓" size={34} variant="cyan" />
          </div>
        </div>

        <GlowText
          as="div"
          text={alreadyExists ? 'ALREADY SUBMITTED' : 'TASKS SUBMITTED SUCCESSFULLY'}
          variant="cyan"
          style={{ fontSize: 'clamp(15px,3.4vw,24px)' }}
        />

        <p className="font-mono-hud text-fluid-md max-w-md" style={{ color: 'var(--text)' }}>
          {alreadyExists
            ? 'This wallet already has an application on file. One entry per wallet — no need to submit again.'
            : 'The team will verify your tasks. If you are lucky, you will be whitelisted for the GTD phase.'}
        </p>

        <div className="hud-chip">
          <span className="status-dot" />
          <span className="font-mono-hud text-dim text-xs">WALLET</span>
          <GlowText text={`${wallet.slice(0, 6)}…${wallet.slice(-4)}`} size={14} variant="violet" mono />
        </div>

        <div
          className="mech-panel w-full max-w-md"
          style={{ padding: '14px 16px', borderStyle: 'dashed' }}
        >
          <div className="font-mono-hud text-dim" style={{ fontSize: 11, letterSpacing: '0.12em', marginBottom: 6 }}>
            WHAT HAPPENS NEXT
          </div>
          <p className="font-mono-hud text-dim text-fluid-sm" style={{ margin: 0 }}>
            Verification is manual and takes time. Whitelist spots are limited,
            so completing the tasks is an entry — not a guaranteed spot.
          </p>
        </div>
      </div>
    </Panel>
  );
}

/* ── Main ────────────────────────────────────────────────────────── */
export default function TaskSection() {
  // id -> { status: 'verifying' | 'verified', endsAt: unix seconds }
  const [states, setStates] = useState({});
  const [now, setNow] = useState(() => Math.floor(Date.now() / 1000));

  const [quoteUrl, setQuoteUrl] = useState('');
  const [xHandle, setXHandle] = useState('');
  const [wallet, setWallet] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  // Single shared ticker — one interval for every countdown on the page.
  useEffect(() => {
    const id = setInterval(() => setNow(Math.floor(Date.now() / 1000)), 1000);
    return () => clearInterval(id);
  }, []);

  // Promote finished verifications. Driven by wall-clock timestamps rather
  // than per-task timeouts, so a backgrounded tab (where browsers throttle
  // timers) still lands on the right state when the user returns.
  useEffect(() => {
    setStates((prev) => {
      let changed = false;
      const next = { ...prev };
      for (const [id, s] of Object.entries(prev)) {
        if (s.status === 'verifying' && now >= s.endsAt) {
          next[id] = { status: 'verified' };
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [now]);

  const handleOpen = useCallback((task) => {
    window.open(task.url, '_blank', 'noopener,noreferrer');
    setStates((prev) => {
      if (prev[task.id]) return prev; // already running or done
      return { ...prev, [task.id]: { status: 'verifying', endsAt: Math.floor(Date.now() / 1000) + VERIFY_SECONDS } };
    });
  }, []);

  const verifiedCount = useMemo(
    () => TASKS.filter((t) => states[t.id]?.status === 'verified').length,
    [states]
  );
  const verifyingCount = useMemo(
    () => TASKS.filter((t) => states[t.id]?.status === 'verifying').length,
    [states]
  );

  const allDone = verifiedCount === TASKS.length;
  const quoteValid = TWEET_RE.test(quoteUrl.trim());
  const walletValid = EVM_RE.test(wallet.trim());
  const canSubmit = allDone && quoteValid && walletValid && !submitting;

  const handleSubmit = async () => {
    setError(null);
    try {
      setSubmitting(true);
      const res = await submitTasks({
        walletAddress: wallet,
        quoteTweetUrl: quoteUrl,
        xHandle,
        tasksCompleted: TASKS.reduce((a, t) => ({ ...a, [t.id]: states[t.id]?.status === 'verified' }), {}),
      });
      setResult(res);
    } catch (err) {
      setError(err.message || 'Submission failed. Try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (result) {
    return <SuccessScreen wallet={wallet.trim().toLowerCase()} alreadyExists={result.alreadyExists} />;
  }

  const pct = (verifiedCount / TASKS.length) * 100;

  return (
    <div className="space-y-6">

      {/* ═══ Header ═══ */}
      <Panel title="WHITELIST PROTOCOL // GTD APPLICATION" bodyClassName="space-y-5">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
          <div className="space-y-1">
            <GlowText as="div" text="COMPLETE ALL TASKS" variant="cyan" style={{ fontSize: 'clamp(16px,3.2vw,22px)' }} />
            <p className="font-mono-hud text-dim text-fluid-sm max-w-md">
              Each task verifies for {VERIFY_SECONDS} seconds after you open it.
              Finish all {TASKS.length} to unlock wallet submission.
            </p>
          </div>
          <div className="hud-chip shrink-0">
            <span className="status-dot" />
            <GlowText text={`${verifiedCount} / ${TASKS.length}`} size={17} variant="cyan" mono />
          </div>
        </div>

        {/* Progress gauge */}
        <div
          className="w-full h-3 overflow-hidden"
          style={{
            background: 'rgba(0,229,255,0.08)',
            border: '1px solid var(--steel)',
            clipPath: 'polygon(6px 0, 100% 0, 100% calc(100% - 6px), calc(100% - 6px) 100%, 0 100%, 0 6px)',
          }}
        >
          <div
            className={pct >= 100 ? 'progress-near-win' : undefined}
            style={{
              position: 'relative', overflow: 'hidden',
              width: `${pct}%`, height: '100%',
              background: 'linear-gradient(90deg, var(--violet), var(--cyan))',
              boxShadow: '0 0 10px var(--cyan)',
              transition: 'width 0.5s cubic-bezier(.2,.9,.3,1.1)',
            }}
          >
            <div className="progress-shimmer" />
          </div>
        </div>

        {verifyingCount > 0 && (
          <div className="hud-chip w-full justify-center" style={{ borderColor: 'var(--amber)' }}>
            <span className="font-mono-hud text-fluid-sm glow-amber">
              {verifyingCount} task{verifyingCount > 1 ? 's' : ''} verifying — keep this page open
            </span>
          </div>
        )}
      </Panel>

      {/* ═══ Tasks ═══ */}
      <Panel title="TASK QUEUE" bodyClassName="space-y-2.5">
        {TASKS.map((t, i) => {
          const st = states[t.id];
          const remaining = st?.status === 'verifying' ? Math.max(0, st.endsAt - now) : 0;
          return (
            <TaskRow key={t.id} task={t} state={st} remaining={remaining} onOpen={handleOpen} index={i} />
          );
        })}
      </Panel>

      {/* ═══ Submission ═══ */}
      {!allDone ? (
        <Panel title="WALLET SUBMISSION // LOCKED">
          <div className="flex flex-col items-center text-center gap-3 py-7">
            <div
              className="flex items-center justify-center"
              style={{
                width: 52, height: 52, borderRadius: 10,
                border: '2px dashed var(--steel-light)',
                color: 'var(--text-dim)', fontSize: 22,
              }}
            >
              🔒
            </div>
            <GlowText as="div" text="LOCKED" size={14} variant="amber" />
            <p className="font-mono-hud text-dim text-fluid-sm max-w-sm">
              {TASKS.length - verifiedCount} task{TASKS.length - verifiedCount > 1 ? 's' : ''} remaining.
              The wallet form unlocks once every task is verified.
            </p>
          </div>
        </Panel>
      ) : (
        <Panel title="WALLET SUBMISSION // UNLOCKED" bodyClassName="space-y-5">
          <div className="hud-chip w-full justify-center" style={{ padding: '10px 14px' }}>
            <span className="status-dot" />
            <span className="font-mono-hud text-fluid-sm glow-cyan">ALL TASKS VERIFIED</span>
          </div>

          <div>
            <label className="font-mono-hud text-dim text-xs tracking-widest block mb-2">
              QUOTE TWEET LINK <span style={{ color: 'var(--danger)' }}>*</span>
            </label>
            <input
              type="url" inputMode="url"
              placeholder="https://x.com/yourhandle/status/..."
              value={quoteUrl}
              onChange={(e) => setQuoteUrl(e.target.value)}
              className="hud-input"
              style={{ borderColor: quoteUrl && !quoteValid ? 'var(--danger)' : undefined }}
            />
            {quoteUrl && !quoteValid && (
              <p className="font-mono-hud glow-danger mt-1" style={{ fontSize: 12 }}>
                Must be an x.com or twitter.com link.
              </p>
            )}
          </div>

          <div>
            <label className="font-mono-hud text-dim text-xs tracking-widest block mb-2">
              YOUR X HANDLE <span style={{ color: 'var(--text-dim)' }}>(optional)</span>
            </label>
            <input
              type="text" placeholder="@yourhandle"
              value={xHandle} onChange={(e) => setXHandle(e.target.value)}
              className="hud-input"
            />
          </div>

          <div>
            <label className="font-mono-hud text-dim text-xs tracking-widest block mb-2">
              ARC WALLET ADDRESS <span style={{ color: 'var(--danger)' }}>*</span>
            </label>
            <input
              type="text" placeholder="0x…"
              value={wallet} onChange={(e) => setWallet(e.target.value)}
              className="hud-input"
              style={{ borderColor: wallet && !walletValid ? 'var(--danger)' : undefined }}
            />
            {wallet && !walletValid && (
              <p className="font-mono-hud glow-danger mt-1" style={{ fontSize: 12 }}>
                Must be 0x followed by 40 hex characters.
              </p>
            )}
            <p className="font-mono-hud text-dim mt-1" style={{ fontSize: 12 }}>
              This is the wallet that gets whitelisted — make sure you control it.
            </p>
          </div>

          {error && (
            <div className="mech-panel" style={{ padding: '10px 16px', borderColor: 'var(--danger)' }}>
              <span className="font-mono-hud text-fluid-sm glow-danger">{error}</span>
            </div>
          )}

          <RoboticButton
            variant={canSubmit ? 'cyan' : 'ghost'}
            size="lg" fullWidth
            disabled={!canSubmit}
            onClick={handleSubmit}
            className={canSubmit ? 'cta-button' : ''}
          >
            {submitting
              ? 'SUBMITTING…'
              : !quoteValid ? 'PASTE YOUR QUOTE TWEET LINK'
              : !walletValid ? 'ENTER YOUR WALLET ADDRESS'
              : 'SUBMIT APPLICATION'}
          </RoboticButton>
        </Panel>
      )}
    </div>
  );
}
